import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDecimal } from '@/components/sizing/advancedInsightsShared';

type DutyCycleTimelineProps = {
  personaName: string;
  readSeconds: number;
  thinkSeconds: number;
  ttftMs: number;
  decodeTps: number;
  outputTokens: number;
};

export const DutyCycleTimeline: React.FC<DutyCycleTimelineProps> = ({
  personaName,
  readSeconds,
  thinkSeconds,
  ttftMs,
  decodeTps,
  outputTokens,
}) => {
  const ttftSeconds = ttftMs / 1000;
  const streamSeconds = decodeTps > 0 ? outputTokens / decodeTps : 0;
  const total = readSeconds + thinkSeconds + ttftSeconds + streamSeconds;

  const readPct = (readSeconds / total) * 100;
  const thinkPct = (thinkSeconds / total) * 100;
  const ttftPct = (ttftSeconds / total) * 100;
  const streamPct = (streamSeconds / total) * 100;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{personaName}</CardTitle>
        <CardDescription>Duty cycle breakdown showing time allocation</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Timeline Bar */}
        <div className="relative h-12 w-full rounded-lg overflow-hidden border border-border">
          <div
            className="absolute h-full bg-blue-500/80 flex items-center justify-center text-xs font-medium text-white"
            style={{ width: `${readPct}%`, left: 0 }}
          >
            {readPct > 10 && 'Read'}
          </div>
          <div
            className="absolute h-full bg-purple-500/80 flex items-center justify-center text-xs font-medium text-white"
            style={{ width: `${thinkPct}%`, left: `${readPct}%` }}
          >
            {thinkPct > 10 && 'Think'}
          </div>
          <div
            className="absolute h-full bg-amber-500/80 flex items-center justify-center text-xs font-medium text-white"
            style={{ width: `${ttftPct}%`, left: `${readPct + thinkPct}%` }}
          >
            {ttftPct > 5 && 'TTFT'}
          </div>
          <div
            className="absolute h-full bg-emerald-500/80 flex items-center justify-center text-xs font-medium text-white"
            style={{ width: `${streamPct}%`, left: `${readPct + thinkPct + ttftPct}%` }}
          >
            {streamPct > 10 && 'Stream'}
          </div>
        </div>

        {/* Legend */}
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-blue-500/80" />
            <span className="text-muted-foreground">Read: {formatDecimal(readSeconds, 1)}s</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-purple-500/80" />
            <span className="text-muted-foreground">Think: {formatDecimal(thinkSeconds, 1)}s</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-amber-500/80" />
            <span className="text-muted-foreground">TTFT: {formatDecimal(ttftSeconds, 2)}s</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-emerald-500/80" />
            <span className="text-muted-foreground">Stream: {formatDecimal(streamSeconds, 2)}s</span>
          </div>
        </div>

        {/* Summary */}
        <div className="pt-2 border-t border-border text-sm text-muted-foreground">
          <div className="flex justify-between">
            <span>Total Cycle Duration:</span>
            <span className="font-medium text-foreground">{formatDecimal(total, 2)}s</span>
          </div>
          <div className="flex justify-between">
            <span>Service Time (TTFT + Stream):</span>
            <span className="font-medium text-foreground">{formatDecimal(ttftSeconds + streamSeconds, 2)}s</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
